/**
This module includes backported functions from Immutable.js 4.x
that help dealing with mixed POJO/Immutable data. The code here
follows the Immutable code quite directly. For more context,
you can browse the code at
https://github.com/facebook/immutable-js/tree/v4.0.0-rc.10/src
 */
import {Collection, Map} from 'immutable';
import {isFunction, isPlainObject, isArrayLike} from 'lodash';

type KeyPathType =
  | ReadonlyArray<string | number>
  | Collection<any, string | number>;

const emptyMap = () => Map();

// All Immutable data structures are instances of Collection
export const isImmutable = (maybeImmutable: unknown): boolean =>
  // @ts-expect-error - TS2708 - Cannot use namespace 'Collection' as a value.
  maybeImmutable instanceof Collection;

const isDataStructure = (value: unknown): boolean =>
  typeof value === 'object' &&
  (isImmutable(value) || Array.isArray(value) || isPlainObject(value));

export const has = (collection: unknown, key: string | number): boolean => {
  if (isImmutable(collection)) {
    // @ts-expect-error - TS18046 - 'collection' is of type 'unknown'.
    return collection.has(key);
  } else if (collection && typeof collection === 'object') {
    if (isDataStructure(collection)) {
      // Array or Object
      return Object.hasOwn(collection, key);
    }
  }
  return false;
};

function coerceKeyPath(keyPath: KeyPathType): Array<string | number> {
  if (isArrayLike(keyPath) && typeof keyPath !== 'string') {
    // @ts-expect-error - TS4104 - The type 'readonly (string | number)[]' is 'readonly' and cannot be assigned to the mutable type '(string | number)[]'.
    return keyPath;
  }
  if (isImmutable(keyPath)) {
    // This doesn't guard against unordered structures.
    return keyPath.toArray();
  }
  throw new TypeError(
    `Invalid keyPath: expected Ordered Collection or Array: ${String(keyPath)}`
  );
}

function arrCopy(arr: any, offset: undefined | number) {
  offset = offset || 0;
  const len = Math.max(0, arr.length - offset);
  const newArr = new Array(len);
  for (let ii = 0; ii < len; ii++) {
    newArr[ii] = arr[ii + offset];
  }
  return newArr;
}

function shallowCopy(from: any) {
  if (Array.isArray(from)) {
    // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
    return arrCopy(from);
  }
  const to: Record<string, any> = {};
  for (const key in from) {
    if (Object.hasOwn(from, key)) {
      to[key] = from[key];
    }
  }
  return to;
}

function quoteString(value: number | string) {
  try {
    return typeof value === 'string' ? JSON.stringify(value) : String(value);
  } catch (_ignoreError: any) {
    return JSON.stringify(value);
  }
}

export const get = (
  collection: unknown,
  key: string | number,
  notSetValue?: any
): any => {
  if (isImmutable(collection)) {
    // @ts-expect-error - TS18046 - 'collection' is of type 'unknown'.
    return collection.get(key, notSetValue);
  }
  if (has(collection, key)) {
    // @ts-expect-error - TS18046 - 'collection' is of type 'unknown'.
    if (isFunction(collection.get)) {
      // @ts-expect-error - TS18046 - 'collection' is of type 'unknown'.
      return collection.get(key);
    }
    // @ts-expect-error - TS18046 - 'collection' is of type 'unknown'.
    return collection[key];
  }
  return notSetValue;
};

const _NOT_SET: Record<string, any> = {};

export const getIn = (
  collection: unknown,
  searchKeyPath: KeyPathType,
  notSetValue?: any
): any => {
  const keyPath = coerceKeyPath(searchKeyPath);
  let i = 0;
  while (i !== keyPath.length) {
    // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
    collection = get(collection, keyPath[i++], _NOT_SET);
    if (collection === _NOT_SET) {
      return notSetValue;
    }
  }
  return collection;
};

export const set = (collection: any, key: string | number, value: any): any => {
  if (!isDataStructure(collection)) {
    throw new TypeError(
      `Cannot update non-data-structure value: ${collection}`
    );
  }
  if (isImmutable(collection)) {
    if (!collection.set) {
      throw new TypeError(
        `Cannot update immutable value without .set() method: ${collection}`
      );
    }
    return collection.set(key, value);
  }
  if (Object.hasOwn(collection, key) && value === collection[key]) {
    return collection;
  }
  const collectionCopy = shallowCopy(collection);
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string | number' can't be used to index type 'any[] | Record<string, any>'.
  collectionCopy[key] = value;
  return collectionCopy;
};

export const remove = (collection: any, key: string | number): any => {
  if (!isDataStructure(collection)) {
    throw new TypeError(
      `Cannot update non-data-structure value: ${collection}`
    );
  }
  if (isImmutable(collection)) {
    if (!collection.remove) {
      throw new TypeError(
        `Cannot update immutable value without .remove() method: ${collection}`
      );
    }
    return collection.remove(key);
  }
  if (!Object.hasOwn(collection, key)) {
    return collection;
  }
  const collectionCopy = shallowCopy(collection);
  if (Array.isArray(collectionCopy)) {
    // @ts-expect-error - TS2769 - No overload matches this call.
    collectionCopy.splice(key, 1);
  } else {
    //eslint-disable-next-line webflow/no-delete
    delete collectionCopy[key];
  }
  return collectionCopy;
};

export const updateIn = (
  collection: unknown,
  keyPath: KeyPathType,
  notSetValue: any,
  updater?: any
): any => {
  if (!updater) {
    updater = notSetValue;
    notSetValue = undefined;
  }
  const updatedValue = updateInDeeply(
    isImmutable(collection),
    collection,
    coerceKeyPath(keyPath),
    0,
    notSetValue,
    updater
  );
  return updatedValue === _NOT_SET ? notSetValue : updatedValue;
};

export const update = (
  collection: unknown,
  key: string | number,
  notSetValue: any,
  updater: any
): any => {
  return updateIn(collection, [key], notSetValue, updater);
};

function updateInDeeply(
  inImmutable: boolean,
  existing: any,
  keyPath: Array<string | number>,
  i: number,
  notSetValue: any,
  updater: any
): any {
  const wasNotSet = existing === _NOT_SET;
  if (i === keyPath.length) {
    const existingValue = wasNotSet ? notSetValue : existing;
    const newValue = updater(existingValue);
    return newValue === existingValue ? existing : newValue;
  }
  if (!wasNotSet && !isDataStructure(existing)) {
    throw new TypeError(
      'Cannot update within non-data-structure value in path ' +
        `[${keyPath.slice(0, i).map(quoteString).join(', ')}]: ${existing}`
    );
  }
  const key = keyPath[i];
  // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
  const nextExisting = wasNotSet ? _NOT_SET : get(existing, key, _NOT_SET);
  const nextUpdated = updateInDeeply(
    nextExisting === _NOT_SET ? inImmutable : isImmutable(nextExisting),
    nextExisting,
    keyPath,
    i + 1,
    notSetValue,
    updater
  );
  return nextUpdated === nextExisting
    ? existing
    : nextUpdated === _NOT_SET
      ? // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
        remove(existing, key)
      : set(
          wasNotSet ? (inImmutable ? emptyMap() : {}) : existing,
          // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
          key,
          nextUpdated
        );
}

export const setIn = (
  collection: any,
  keyPath: KeyPathType,
  value: any
): any => {
  return updateIn(collection, keyPath, _NOT_SET, () => value);
};
